import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { ageLabel } from "@/lib/age";
import { getRememberedFamily, forgetFamily, getTrustedSince } from "@/lib/familyDevice";
import FamilyFormFields from "@/components/FamilyFormFields";
import FamilyPasswordCard from "@/components/FamilyPasswordCard";
import TrustedDevicesCard from "@/components/TrustedDevicesCard";
import { PhotoGrid, PhotoUploader } from "@/pages/Gallery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Leaf,
  CheckCircle2,
  XCircle,
  Calendar as CalIcon,
  MapPin,
  FileDown,
  Heart,
  Share2,
  Copy,
  Home,
  Mail,
  MessageSquare,
} from "lucide-react";

const EMPTY = {
  first_name: "",
  last_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  zip_code: "",
  phone: "",
  email: "",
  birth_date: "",
  father_name: "",
  mother_name: "",
  children: [],
  notes: "",
  reunion_fee: 50,
};

function MemberChip({ name, paid, amount, age }) {
  const amt = Number(amount) || 0;
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full pl-3 pr-1 py-1 text-xs border ${
        paid
          ? "bg-[#1a2947]/[0.06] border-[#1a2947]/25 text-stone-800"
          : "bg-white border-stone-200 text-stone-500"
      }`}
      data-testid={`member-chip-${name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="font-medium">{name}</span>
      {age && (
        <span
          className="rounded-full bg-[#eef2fb] border border-[#c7d3ea] px-1.5 py-0.5 text-[10px] font-semibold text-[#1a2947] tabular-nums"
          data-testid={`member-chip-age-${name.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {age}
        </span>
      )}
      <span
        className={`rounded-full px-2 py-0.5 text-[10px] tracking-wider uppercase font-semibold ${
          paid ? "bg-[#1a2947] text-white" : "bg-stone-100 text-stone-500"
        }`}
      >
        {paid ? `Paid $${amt.toFixed(0)}` : "Unpaid"}
      </span>
    </div>
  );
}

export default function FamilyPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [payment, setPayment] = useState({
    paid: false,
    amount: 0,
    date: null,
    notes: "",
  });
  const [events, setEvents] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const loadPhotos = () => api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});

  const shareReunionSite = async () => {
    const siteUrl = window.location.origin;
    const message = `You're invited to the Bailey Family Reunion — July 21, 2028!\n\nSave the date and follow along at: ${siteUrl}\n\nSee you there. 💙`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Bailey Family Reunion",
          text: message,
          url: siteUrl,
        });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Invite copied — paste it into a text or email.");
    } catch {
      toast.error("Couldn't copy. Long-press to copy this: " + siteUrl);
    }
  };

  const cardUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/family/${token}`
      : "";

  const copyCardLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      toast.success("Link copied — bookmark it or paste it somewhere safe.");
    } catch {
      window.prompt("Copy this link and save it somewhere safe:", cardUrl);
    }
  };

  const [emailingLink, setEmailingLink] = useState(false);
  const emailMyLink = async () => {
    setEmailingLink(true);
    try {
      const r = await api.post(`/families/token/${token}/email-link`);
      const to = r.data?.to || [];
      toast.success(
        to.length > 1
          ? `Link emailed to both ${to.join(" and ")}.`
          : `Link emailed to ${to[0] || "your email"}.`
      );
    } catch (e) {
      toast.error(
        formatApiError(e.response?.data?.detail) ||
          "Couldn't email the link right now."
      );
    } finally {
      setEmailingLink(false);
    }
  };

  const textMeLink = () => {
    const phone = (data.phone || "").replace(/[^\d+]/g, "");
    if (!phone) {
      toast.error(
        "Add a phone number below and save, then try again."
      );
      return;
    }
    const body = `Your Bailey Family Reunion card: ${cardUrl}`;
    // `sms:` URI with `?&body=` works on both iOS and Android
    window.location.href = `sms:${phone}?&body=${encodeURIComponent(body)}`;
  };

  useEffect(() => {
    Promise.all([api.get(`/families/token/${token}`), api.get("/events"), api.get("/photos")])
      .then(([r, ev, ph]) => {
        setData({ ...EMPTY, ...r.data });
        setPayment(r.data.payment || {});
        setEvents(ev.data || []);
        setPhotos(ph.data || []);
      })
      .catch((e) =>
        setError(
          formatApiError(e.response?.data?.detail) || "Link is invalid."
        )
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload.payment;
      delete payload.rsvps;
      await api.put(`/families/token/${token}`, payload);
      toast.success("Saved. Thank you!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-stone-500">
        Loading your family card...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-white border border-[#e6e2d6] rounded-2xl p-10">
          <XCircle className="h-10 w-10 text-[#1a2947] mx-auto mb-4" />
          <h1 className="font-serif text-2xl mb-2">This link isn't working</h1>
          <p className="text-stone-600 mb-6">{error}</p>
          <Link
            to="/"
            className="text-[#1a2947] underline"
            data-testid="error-home-link"
          >
            Go home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="max-w-3xl mx-auto px-6 md:px-10 pt-10 pb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/bailey-logo.png" alt="Bailey Family Reunion" className="h-10 w-10 rounded-full object-cover ring-2 ring-[#1a2947]/10" />
          <span className="font-serif text-2xl text-stone-900">Bailey Family Reunion</span>
        </div>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#e6e2d6] bg-white px-4 py-2 text-sm font-medium text-[#1a2947] hover:bg-[#1a2947] hover:text-white transition-colors"
          data-testid="portal-home-link"
        >
          <Home className="h-4 w-4" /> Home
        </Link>
        {getRememberedFamily() === token && (
          <div className="ml-2 flex flex-col items-end" data-testid="portal-trust-cluster">
            <button
              type="button"
              onClick={() => {
                forgetFamily();
                toast.success("This device forgot the login. Password required next time.");
                window.location.href = "/";
              }}
              className="text-xs text-stone-500 hover:text-red-600 underline"
              data-testid="portal-forget-device-btn"
            >
              Forget this device
            </button>
            {getTrustedSince() && (
              <span
                className="text-[10px] text-stone-400 mt-0.5 tabular-nums"
                data-testid="portal-trusted-since"
                title={new Date(getTrustedSince()).toString()}
              >
                Trusted since{" "}
                {new Date(getTrustedSince()).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            )}
          </div>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <div className="label-eyebrow mb-3">Family card</div>
        <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight mb-2">
          Keep your details fresh
          <span className="italic text-[#1a2947]">.</span>
        </h1>
        <p className="text-stone-600 mb-6 max-w-xl">
          Update your contact info, list your children, and settle your reunion
          dues. Changes save when you press the button at the bottom.
        </p>

        <div
          className="bg-[#fff8e1] border border-[#e7d59a] text-[#6b5417] rounded-xl px-4 py-3 mb-6 text-sm font-medium"
          data-testid="portal-primary-note"
        >
          Please list the Bailey family member first.
        </div>

        <div
          className="bg-white border border-[#e6e2d6] rounded-2xl p-5 mb-6"
          data-testid="portal-password-card"
        >
          <FamilyPasswordCard
            token={token}
            hasPassword={!!data.has_password}
            loginEmail={data.login_email || ""}
            onChanged={(patch) => setData((prev) => ({ ...prev, ...patch }))}
          />
        </div>

        <TrustedDevicesCard token={token} />

        <div
          className="bg-white border border-[#e6e2d6] rounded-2xl p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4"
          data-testid="portal-save-link-card"
        >
          <div className="min-w-0 flex-1">
            <div className="label-eyebrow text-[#1a2947] mb-1">
              Save for access to your family card
            </div>
            <div
              className="text-sm text-stone-600 truncate font-mono"
              title={cardUrl}
              data-testid="portal-save-link-url"
            >
              {cardUrl}
            </div>
            <p className="text-xs text-stone-500 mt-2">
              Bookmark this page or email it to yourself so you can update your
              info anytime.
            </p>
          </div>
          <button
            type="button"
            onClick={copyCardLink}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#1a2947] text-white text-sm font-semibold px-4 py-2 hover:bg-[#0f1a2e] transition-colors"
            data-testid="portal-save-link-copy-btn"
          >
            <Copy className="h-4 w-4" /> Copy link
          </button>
          <button
            type="button"
            onClick={emailMyLink}
            disabled={emailingLink}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full border-2 border-[#1a2947] text-[#1a2947] text-sm font-semibold px-4 py-2 hover:bg-[#1a2947] hover:text-white transition-colors disabled:opacity-60"
            data-testid="portal-save-link-email-btn"
          >
            <Mail className="h-4 w-4" />
            {emailingLink ? "Sending…" : "Email me this link"}
          </button>
          <button
            type="button"
            onClick={textMeLink}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full border-2 border-[#1a2947] text-[#1a2947] text-sm font-semibold px-4 py-2 hover:bg-[#1a2947] hover:text-white transition-colors"
            data-testid="portal-save-link-text-btn"
          >
            <MessageSquare className="h-4 w-4" /> Text me this link
          </button>
        </div>

        <div className="mb-10 flex flex-wrap gap-3">
          <a
            href={`${API_BASE}/families/token/${token}/directory-pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#1a2947] text-[#1a2947] font-semibold px-5 py-2.5 hover:bg-[#1a2947] hover:text-white transition-colors text-sm"
            data-testid="portal-directory-pdf-link"
          >
            <FileDown className="h-4 w-4" /> Family Directory (PDF)
          </a>
          <a
            href={`${API_BASE}/families/token/${token}/phone-tree-pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#1a2947] text-[#1a2947] font-semibold px-5 py-2.5 hover:bg-[#1a2947] hover:text-white transition-colors text-sm"
            data-testid="portal-phone-tree-pdf-link"
          >
            <FileDown className="h-4 w-4" /> Phone Tree (PDF)
          </a>
          <a
            href={`${API_BASE}/families/token/${token}/wallet-card-pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#1a2947] text-[#1a2947] font-semibold px-5 py-2.5 hover:bg-[#1a2947] hover:text-white transition-colors text-sm"
            data-testid="portal-wallet-card-pdf-link"
          >
            <FileDown className="h-4 w-4" /> Wallet Card (PDF)
          </a>
          <Link
            to={`/memoriam?add=1&token=${token}&by=${encodeURIComponent(
              `${(data.first_name || "").trim()} ${(data.last_name || "").trim()}`.trim()
            )}`}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#7CB2E8] text-[#1a2947] font-semibold px-5 py-2.5 hover:bg-[#7CB2E8]/10 transition-colors text-sm"
            data-testid="portal-memoriam-link"
          >
            <Heart className="h-4 w-4 text-[#7CB2E8]" /> Post to In Memoriam
          </Link>
          <button
            type="button"
            onClick={shareReunionSite}
            className="inline-flex items-center gap-2 rounded-full bg-[#1a2947] text-white font-semibold px-5 py-2.5 hover:bg-[#0f1a2e] transition-colors text-sm shadow-[0_10px_24px_-8px_rgba(26,41,71,0.55)]"
            data-testid="portal-share-site-btn"
          >
            <Share2 className="h-4 w-4" /> Share Reunion Site
          </button>
        </div>

        {/* Payment status card */}
        <div
          className="bg-white border border-[#e6e2d6] rounded-2xl p-6 mb-6"
          data-testid="portal-payment-card"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
            <div className="min-w-0">
              <div className="label-eyebrow mb-1">Reunion Payment</div>
              <div className="font-serif text-2xl text-stone-900">
                {(() => {
                  const primaryPaid = !!payment.paid;
                  const partnerPaid = !!data.partner_paid;
                  const otherAdultPaid = !!data.other_adult_paid;
                  const hasPartner = !!data.partner_first_name;
                  const hasOtherAdult = !!data.other_adult_first_name;
                  const kids = data.children || [];
                  const allPaid =
                    primaryPaid &&
                    (!hasPartner || partnerPaid) &&
                    (!hasOtherAdult || otherAdultPaid) &&
                    kids.every((c) => c.paid);
                  const somePaid =
                    primaryPaid ||
                    partnerPaid ||
                    otherAdultPaid ||
                    kids.some((c) => c.paid);
                  if (allPaid && (primaryPaid || partnerPaid || otherAdultPaid || kids.length))
                    return "All paid — thank you!";
                  if (somePaid) return "Partially paid";
                  return "Not yet received";
                })()}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {(() => {
                const total =
                  (payment.paid ? Number(payment.amount) || 0 : 0) +
                  (data.partner_paid ? Number(data.partner_paid_amount) || 0 : 0) +
                  (data.other_adult_paid ? Number(data.other_adult_paid_amount) || 0 : 0) +
                  (data.children || []).reduce(
                    (s, c) => s + (c.paid ? Number(c.paid_amount) || 0 : 0),
                    0
                  );
                return total > 0 ? (
                  <div className="text-right">
                    <div className="label-eyebrow">Collected</div>
                    <div className="font-serif text-2xl text-[#1a2947]">
                      ${total.toFixed(2)}
                    </div>
                  </div>
                ) : (
                  <span className="rounded-full px-3 py-1 text-xs bg-[#eae5d9] text-stone-700">
                    Contact your organizer to pay
                  </span>
                );
              })()}
            </div>
          </div>

          {/* Per-member paid chips */}
          <div className="border-t border-[#eae5d9] pt-4">
            <div className="label-eyebrow mb-3">Household members</div>
            <div className="flex flex-wrap gap-2" data-testid="portal-member-chips">
              <MemberChip
                name={
                  `${data.first_name || ""} ${data.last_name || ""}`.trim() ||
                  "Primary adult"
                }
                paid={!!payment.paid}
                amount={payment.amount}
              />
              {data.partner_first_name && (
                <MemberChip
                  name={`${data.partner_first_name} ${
                    data.partner_last_name || data.last_name || ""
                  }`.trim()}
                  paid={!!data.partner_paid}
                  amount={data.partner_paid_amount}
                />
              )}
              {data.other_adult_first_name && (
                <MemberChip
                  name={`${data.other_adult_first_name} ${
                    data.other_adult_last_name || data.last_name || ""
                  }`.trim()}
                  paid={!!data.other_adult_paid}
                  amount={data.other_adult_paid_amount}
                />
              )}
              {(data.children || [])
                .filter((c) => (c.first_name || "").trim())
                .map((c, i) => (
                  <MemberChip
                    key={`chip-${i}`}
                    name={c.first_name}
                    paid={!!c.paid}
                    amount={c.paid_amount}
                    age={ageLabel(c.birth_date)}
                  />
                ))}
            </div>
            <p className="text-xs text-stone-500 mt-3">
              Your organizer updates these when payment arrives — nothing to do here.
            </p>
          </div>
        </div>

        {/* Payment history */}
        {(data.payments || []).length > 0 && (
          <div
            className="bg-white border border-[#e6e2d6] rounded-2xl p-6 mb-6"
            data-testid="portal-payment-history"
          >
            <div className="label-eyebrow mb-4">Payments received</div>
            <div className="space-y-1">
              {[...(data.payments || [])]
                .sort((a, b) => (a.date < b.date ? 1 : -1))
                .map((e) => (
                  <div
                    key={e.id}
                    className="flex items-center gap-3 text-sm py-2 border-b border-[#eae5d9] last:border-0"
                    data-testid={`portal-payment-row-${e.id}`}
                  >
                    <div className="w-28 text-stone-500 tabular-nums">{e.date}</div>
                    <div className="w-24 font-medium text-stone-800 tabular-nums">
                      ${Number(e.amount).toFixed(2)}
                    </div>
                    <div className="flex-1 min-w-0 text-stone-500 truncate">
                      {e.note || ""}
                    </div>
                  </div>
                ))}
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#eae5d9]">
              <span className="label-eyebrow">Total received</span>
              <span
                className="font-serif text-2xl text-[#1a2947]"
                data-testid="portal-payment-history-total"
              >
                $
                {(data.payments || [])
                  .reduce((s, e) => s + (Number(e.amount) || 0), 0)
                  .toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Reunion Events (read-only) */}
        {events.length > 0 && (
          <div
            className="bg-white border border-[#e6e2d6] rounded-2xl p-6 md:p-8 mb-6"
            data-testid="portal-events"
          >
            <div className="label-eyebrow mb-1">Reunion Events</div>
            <p className="text-sm text-stone-500 mb-6">
              Mark your calendar — details below.
            </p>
            <div className="space-y-3">
              {events.map((ev) => (
                <div
                  key={ev._id}
                  className="border border-[#e6e2d6] rounded-xl p-4 bg-[#faf7ef]"
                  data-testid={`event-card-${ev._id}`}
                >
                  <div className="font-serif text-xl text-stone-900">
                    {ev.name}
                  </div>
                  <div className="text-xs text-stone-500 mt-1 flex items-center gap-3 flex-wrap">
                    {ev.date && (
                      <span className="flex items-center gap-1">
                        <CalIcon className="h-3 w-3" /> {ev.date}
                        {ev.time ? ` · ${ev.time}` : ""}
                      </span>
                    )}
                    {ev.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {ev.location}
                      </span>
                    )}
                  </div>
                  {ev.description && (
                    <p className="text-sm text-stone-600 mt-2">
                      {ev.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <FamilyFormFields value={data} onChange={setData} />

        {/* Family Album */}
        <div className="mt-10">
          <div className="label-eyebrow mb-1">Memories</div>
          <h2 className="font-serif text-3xl text-stone-900 tracking-tight mb-2">
            Family Album
          </h2>
          <p className="text-stone-600 mb-6 max-w-xl">
            Share a photo with the whole family — old scans, new selfies, all
            welcome.
          </p>
          <div className="mb-6">
            <PhotoUploader
              endpoint={`/photos/by-token/${token}`}
              onUploaded={loadPhotos}
              testid="family-photo-upload"
            />
          </div>
          <PhotoGrid
            photos={photos}
            adminMode={false}
            onImageClick={(p) => setLightbox(p)}
          />
        </div>

        <div className="mt-8 pb-6 flex items-center justify-end gap-3 sticky bottom-4 z-20">
          <Button
            onClick={save}
            disabled={saving}
            className="w-full sm:w-auto rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill px-6 h-12 shadow-[0_10px_24px_-8px_rgba(26,41,71,0.55)]"
            data-testid="family-save-btn"
          >
            {saving ? "Saving..." : "Save my changes"}
          </Button>
        </div>
      </main>

      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="bg-black border-none max-w-4xl p-0">
          {lightbox && (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${lightbox.url}`}
              alt={lightbox.caption || ""}
              className="w-full max-h-[85vh] object-contain"
            />
          )}
          {lightbox?.caption && (
            <div className="text-white text-center text-sm p-4 bg-black">
              {lightbox.caption}
              <div className="text-stone-400 text-xs mt-1">
                — {lightbox.uploader_name}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
